import type { PoiCategory } from '$lib/components/pois/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { Page } from '@sveltejs/kit';
import type { AdId } from '../types';
import { isMobile } from '$lib/services/mediaQuery/screenSize';
import {
	isCorridorPageContext,
	isRouteResultsPage,
	isRouteSearchPageContext,
	isTrafficPage,
	isZoiPageContext,
} from '$lib/services/urls/routeIds';
import { routesPagename } from '$lib/services/urls/routesPagename';
import { get } from 'svelte/store';

export const datasheetOrangePageIdByCategory: Record<PoiCategory, string> = {
	ACCOMMODATION: 'hotel.datasheet',
	TOURISM: 'tourisme.datasheet',
	RESTAURANT: 'restaurant.datasheet',
	EV_SERVICE: 'bornesderecharge.datasheet',
	PARKING: '', // No Datasheet page for parking
	REST_STOP: '', // No Datasheet page for rest stop
	SERVICE_GAS_STATION: '', // No Datasheet page for gas station
	PARTNER: '', // No Datasheet page for partner
	PARTNER2: '', // No Datasheet page for partner2
};

export const poiListOrangePageIdByCategory: Record<PoiCategory, string> = {
	ACCOMMODATION: 'hotel.resultat',
	TOURISM: 'tourisme.resultat',
	RESTAURANT: 'restaurant.resultat',
	EV_SERVICE: 'bornesderecharge.resultat',
	PARKING: 'parking.resultat',
	REST_STOP: 'stationservice.resultat', // Use same page id as gas station
	SERVICE_GAS_STATION: 'stationservice.resultat',
	PARTNER: 'partner.resultat',
	PARTNER2: 'partner2.resultat',
};

/**
 * Returns the Orange Ad ID prefix for a given Ad ID.
 *
 * The orange ID prefix is composed of the following parts:
 * - `via`: The Orange Ad network identifier for viamichelin
 * - a ad type identifier (e.g. `r1` for desktop, `r1m` for mobile)
 * - a size identifier (e.g. `728x90`, `300x250`)
 */
export function getOrangeAdIdPrefix(adId: AdId) {
	const isMobileDevice = get(isMobile);
	const devicePrefix = isMobileDevice ? 'r1m' : 'r1';

	if (adId === 'map-banner') {
		return 'via_b2_728x90';
	} else if (adId === 'map-banner-1000') {
		return 'via_b2_1000x90';
	} else if (adId === 'poi-list-banner') {
		return 'via_b1m_320x100';
	} else if (
		adId === 'datasheet'
		|| adId === 'poi-list'
		|| adId === 'itinerary-results-loading'
		|| adId === 'traffic'
	) {
		return `via_${devicePrefix}_300x250`;
	} else if (adId === 'tracking-iti-result-1') {
		return isMobileDevice ? 'via_i1m_1x1' : 'via_i1_1x1';
	} else if (adId === 'tracking-iti-result-2') {
		return isMobileDevice ? 'via_i2m_1x1' : 'via_i2_1x1';
	} else if (adId === 'tracking-iti-result-3') {
		return isMobileDevice ? 'via_i3m_1x1' : 'via_i3_1x1';
	} else if (adId === 'poi-card-native-ad') {
		return 'via_nat_3a_1x1';
	}
}

/**
 * Returns the Orange Ad Page ID for a given Ad ID and page context.
 *
 * The Orange Ad Page ID is used to identify the page context in which the ad is displayed.
 */
export function getOrangeAdPageId(adId: AdId, searchParams: SearchParams, page: Page) {
	if ((page.data.pagename) === 'homepage') {
		return 'hp';
	}

	if (isTrafficPage(page.route)) {
		return 'infotrafic';
	}

	if (adId === 'poi-card-native-ad') {
		return 'itineraire.resultat';
	}

	if (isCorridorPageContext(page.route)) {
		return 'itineraire.route.corridor';
	}

	if (isRouteSearchPageContext(page.route) || isZoiPageContext(page.route)) {
		return routesPagename.ROUTE_EXPLORATION;
	}

	if (adId === 'itinerary-results-loading') {
		return 'itineraire.loading';
	}

	if (adId.startsWith('tracking-iti-result')) {
		return 'itineraire.resultat';
	}

	if (isRouteResultsPage(page.route)) {
		return 'itineraire.resultat';
	}

	if (searchParams.poiCategories?.length === 1) {
		const category = searchParams.poiCategories[0];
		return poiListOrangePageIdByCategory[category];
	}

	return 'carte.resultat';
}

export function getOrangeAdId(adId: AdId, searchParams: SearchParams, page: Page) {
	const pageId = getOrangeAdPageId(adId, searchParams, page);
	const prefix = getOrangeAdIdPrefix(adId);

	return `${prefix}_${pageId}`;
}
